import {Pages} from 'shared/config/model/pages';
import {AppUrls} from 'shared/config/model/urls';

import React, {useState} from 'react';
import {Helmet} from 'react-helmet-async';
import cn from 'classnames';
import {unwrapResult} from '@reduxjs/toolkit';
import {toast} from 'react-toastify';
import {useDispatch} from 'react-redux';
import {generatePath} from 'react-router-dom';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';

import BackButton from 'shared/ui/IconButton/BackButton';
import useBreakpoint from 'shared/lib/hooks/useBreakpoint';
import {IUserOutput, passwordResetRequest} from 'entities/user';
import { isTranslate } from 'shared/lib/lang';
import {TypedDispatch} from 'shared/model/types';

import ProfileForm from './ProfileForm';
import PasswordReset from './PasswordReset';
import ChangeEmailButton from './ChangeEmail';
import ChangeEmailModal from './ChangeEmailModal';

import UserDeleteModal from './UserDeleteModal/UserDeleteModal';

import styles from './Profile.module.scss';

interface IProfileProps {
    user: IUserOutput,
}

const Profile: React.FC<IProfileProps> = ({user}) => {
    const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
    const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
    const dispatch = useDispatch<TypedDispatch>();
    const breakpoint = useBreakpoint();

    async function onPasswordResetRequest(e: React.MouseEvent<HTMLDivElement>) {
        e.preventDefault();
        dispatch(passwordResetRequest(user?.email ?? '')).then(resultAction => {
            const response = unwrapResult(resultAction);
            toast.info(response?.message);
        });
    }

    return (
        <>
            <Helmet>
                <title>{i18n._(Pages.SETTINGS)} | {t`Overseer`}</title>
            </Helmet>

            <div className={styles.container}>
                {
                    breakpoint !== 'large' && breakpoint !== 'medium' &&
                    <div className={styles.titleBlock}>
                        <BackButton
                            to={generatePath(AppUrls.SETTINGS)}
                            text={t`Назад`}
                        />
                        <h3 className={styles.title}>{t`Настройки`}</h3>
                    </div>
                }

                <div className={cn(
                    styles.containerForm,
                    styles[isTranslate() ? 'enColorsContainer' : 'ruColorsContainer'],
                    styles.widthProfileForm,
                )}>
                    <h3 className={styles.title}>{t`Настройки`}</h3>
                    <ProfileForm user={user} onDeleteUser={() => setDeleteUserModalOpen(true)} />
                </div>
                <div className={styles.container_block_change}>
                    <div
                        className={cn(
                            styles.containerForm,
                            styles.widthChangeForm,
                            styles[isTranslate() ? 'enColorsContainerChange' : 'ruColorsContainerChange'],
                        )}
                        onClick={(e) => onPasswordResetRequest(e)}
                    >
                        <PasswordReset/>
                    </div>
                    {
                        (user.email_confirmed || user.last_email_error !== null) &&
                        <div
                            onClick={() => setIsChangeEmailModalOpen(true)}
                            className={cn(
                                styles.containerForm,
                                styles.widthChangeForm,
                                styles[isTranslate() ? 'enColorsContainerChange' : 'ruColorsContainerChange'],
                            )}
                        >
                            <ChangeEmailButton/>
                        </div>
                    }
                </div>
                <ChangeEmailModal
                    open={isChangeEmailModalOpen}
                    handleClose={() => setIsChangeEmailModalOpen(false)}
                />
            </div>

            <UserDeleteModal
                item={user}
                open={deleteUserModalOpen}
                handleClose={() => setDeleteUserModalOpen(false)}
            />
        </>
    );
};

export default Profile;